/**
 * AccountService
 *
 * RESPONSIBILITIES:
 * - Account balance and transaction ledger management
 * - Position lifecycle (buy/sell/expire positions)
 * - Portfolio value and PnL calculations
 * - Position settlement at expiry
 * - Real-time account state updates and notifications
 *
 * SHOULD NOT:
 * - Calculate option prices or volatility (delegates to OptionService/VolatilityService)
 * - Perform Black-Scholes calculations or option mathematics
 * - Handle order management or matching (delegates to OrderService)
 * - Store or manage price feeds
 */

import { getPriceService } from '@hooks/usePrices';
import type { Asset, Position, TxType, LedgerTx, Option } from '@shared/types';

import { OptionCalculator } from './optionService';

// Re-export for backward compatibility
export type { Position, TxType, LedgerTx, Option };

export interface ExpiryGroup {
  positionType: 'CALL' | 'PUT';
  positionCount: number;
  realizedPnL: number;
  averageStrike: number;
  expiryPrice: number;
  asset: Asset;
}

export interface ExpiryEvent {
  totalPnL: number;
  groups: ExpiryGroup[];
}

interface AccountState {
  balance: number;
  positions: Position[];
  ledger: LedgerTx[];
}

const LS_KEYS = {
  balance: 'yv_balance_v1',
  ledger: 'yv_ledger_v1',
  positions: 'yv_positions_v1',
};

export class AccountService {
  private state: AccountState = {
    balance: 1000,
    positions: [],
    ledger: [],
  };
  private subscribers = new Set<(s: AccountState) => void>();
  private expirySubscribers = new Set<(event: ExpiryEvent) => void>();
  private settleIntervalId: number | null = null;
  private priceUnsub: (() => void) | null = null;

  private static instance: AccountService | null = null;
  public static getInstance() {
    AccountService.instance ??= new AccountService();
    return AccountService.instance;
  }

  private constructor() {}

  init() {
    // hydrate
    try {
      const b = localStorage.getItem(LS_KEYS.balance);
      const l = localStorage.getItem(LS_KEYS.ledger);
      const p = localStorage.getItem(LS_KEYS.positions);
      if (b) {
        this.state.balance = JSON.parse(b);
      }
      if (l) {
        this.state.ledger = JSON.parse(l);
      }
      if (p) {
        this.state.positions = JSON.parse(p);
      }
    } catch {
      // noop
    }
    this.save();
    this.startSettlementLoop();
    // Forward price updates so UI can recompute account value
    this.priceUnsub ??= getPriceService().subscribe(() => {
      this.notify();
    });
    this.notify();
  }

  subscribe(cb: (s: AccountState) => void) {
    this.subscribers.add(cb);
    cb(this.getState());
    return () => this.subscribers.delete(cb);
  }

  subscribeToExpiry(cb: (event: ExpiryEvent) => void) {
    this.expirySubscribers.add(cb);
    return () => this.expirySubscribers.delete(cb);
  }

  getState(): AccountState {
    // return shallow copy to avoid external mutation
    const { balance, positions, ledger } = this.state;
    return { balance, positions: [...positions], ledger: [...ledger] };
  }

  private save() {
    try {
      localStorage.setItem(LS_KEYS.balance, JSON.stringify(this.state.balance));
      localStorage.setItem(LS_KEYS.ledger, JSON.stringify(this.state.ledger));
      localStorage.setItem(
        LS_KEYS.positions,
        JSON.stringify(this.state.positions),
      );
    } catch {
      // noop
    }
  }

  private notify() {
    const snapshot = this.getState();
    this.subscribers.forEach(cb => {
      try {
        cb(snapshot);
      } catch {
        // noop
      }
    });
  }

  private notifyExpiry(event: ExpiryEvent) {
    this.expirySubscribers.forEach(cb => {
      try {
        cb(event);
      } catch {
        // noop
      }
    });
  }

  deposit(amount: number) {
    if (!(amount > 0)) {
      return;
    }
    this.pushTx('DEPOSIT', amount);
  }

  buyOption(params: Option): Position | null {
    const totalCost = params.purchasePrice * params.quantity;
    if (totalCost > this.state.balance) {
      return null;
    }

    const position: Position = {
      id: Date.now().toString(),
      asset: params.asset,
      type: params.type,
      strikePrice: params.strikePrice,
      quantity: params.quantity,
      purchasePrice: params.purchasePrice,
      purchaseTimestamp: Date.now(),
      expiryTimestamp: params.expiryTimestamp,
    };

    this.pushTx('BUY_OPTION', -totalCost, {
      asset: position.asset,
      strikePrice: position.strikePrice,
      quantity: position.quantity,
      purchasePrice: position.purchasePrice,
      isCall: position.type === 'CALL',
    });
    this.state.positions = [...this.state.positions, position];
    this.save();
    this.notify();
    return position;
  }

  /**
   * Remove a position from the account (used by OrderService for sells)
   */
  removePosition(positionId: string): void {
    this.state.positions = this.state.positions.filter(
      p => p.id !== positionId,
    );
    this.save();
    this.notify();
  }

  /**
   * Add a transaction to the ledger (used by OrderService)
   */
  pushTx(
    type: TxType,
    amount: number,
    meta?: Record<string, string | number | boolean>,
  ): void {
    const ts = Date.now();
    const newBalance = Math.round((this.state.balance + amount) * 100) / 100;
    const tx: LedgerTx = {
      id: `${ts}-${this.state.ledger.length + 1}`,
      type,
      amount,
      balanceAfter: newBalance,
      timestamp: ts,
      meta: meta ?? {},
    };
    this.state.balance = newBalance;
    this.state.ledger = [...this.state.ledger, tx];
    this.save();
    this.notify();
  }

  private startSettlementLoop() {
    if (this.settleIntervalId) {
      return;
    }
    this.settleIntervalId = window.setInterval(
      () => this.settleExpired(),
      5000,
    );
  }

  private async settleExpired() {
    const now = Date.now();
    const toSettle = this.state.positions.filter(p => p.expiryTimestamp <= now);
    if (toSettle.length === 0) {
      return;
    }

    // Group by asset and type for modal display
    const expiryGroups = new Map<
      string,
      { positions: Position[]; totalPnL: number; expiryPrice?: number }
    >();

    for (const pos of toSettle) {
      const pricePoint = await getPriceService().fetchPriceAtTimestamp(
        pos.asset,
        pos.expiryTimestamp - 1,
      );
      if (!pricePoint) {
        continue;
      }

      const groupKey = `${pos.asset}-${pos.type}`;
      if (!expiryGroups.has(groupKey)) {
        expiryGroups.set(groupKey, {
          positions: [],
          totalPnL: 0,
          expiryPrice: pricePoint.price,
        });
      }
      const group = expiryGroups.get(groupKey)!;

      try {
        const option = OptionCalculator.createOption({
          asset: pos.asset,
          type: pos.type,
          strikePrice: pos.strikePrice,
          expiryTimestamp: pos.expiryTimestamp,
          purchasePrice: pos.purchasePrice,
          quantity: pos.quantity,
          purchaseTimestamp: pos.purchaseTimestamp,
        });

        const { pnlTotal } = OptionCalculator.settleOption(
          option,
          pricePoint.price,
        );

        // net cash at expiry: return principal + PnL
        const totalCost = pos.purchasePrice * pos.quantity;
        const totalPayout = pnlTotal + totalCost;

        this.pushTx('EXPIRE_POSITION', totalPayout, {
          positionId: pos.id,
          asset: pos.asset,
          expiryTimestamp: pos.expiryTimestamp,
          pnl: pnlTotal,
          totalCost: totalCost,
          expiryValue: totalPayout,
        });

        // Add to group for notification
        group.positions.push(pos);
        group.totalPnL += pnlTotal;
      } catch (error) {
        console.warn('Error settling position:', error);
        // Fallback: calculate intrinsic value manually
        const intrinsicValuePerContract =
          pos.type === 'CALL'
            ? Math.max(pricePoint.price - pos.strikePrice, 0)
            : Math.max(pos.strikePrice - pricePoint.price, 0);

        const totalCost = pos.purchasePrice * pos.quantity;
        const totalIntrinsicValue = intrinsicValuePerContract * pos.quantity;
        const pnlTotal = totalIntrinsicValue - totalCost;

        this.pushTx('EXPIRE_POSITION', totalIntrinsicValue, {
          positionId: pos.id,
          asset: pos.asset,
          expiryTimestamp: pos.expiryTimestamp,
          pnl: pnlTotal,
          totalCost: totalCost,
          expiryValue: totalIntrinsicValue,
        });

        // Add to group for notification
        group.positions.push(pos);
        group.totalPnL += pnlTotal;
      }
    }

    // Create a single aggregated expiry event
    if (expiryGroups.size > 0) {
      const groups: ExpiryGroup[] = [];
      let totalPnL = 0;

      for (const [_groupKey, group] of expiryGroups) {
        if (group.positions.length > 0) {
          const firstPos = group.positions[0];
          const averageStrike =
            group.positions.reduce(
              (sum, p) => sum + p.strikePrice * p.quantity,
              0,
            ) / group.positions.reduce((sum, p) => sum + p.quantity, 0);

          groups.push({
            positionType: firstPos.type,
            positionCount: group.positions.reduce(
              (sum, p) => sum + p.quantity,
              0,
            ),
            realizedPnL: group.totalPnL,
            averageStrike,
            expiryPrice: group.expiryPrice ?? 0,
            asset: firstPos.asset,
          });

          totalPnL += group.totalPnL;
        }
      }

      // Send single aggregated event
      if (groups.length > 0) {
        this.notifyExpiry({
          totalPnL,
          groups,
        });
      }
    }

    this.state.positions = this.state.positions.filter(
      p => p.expiryTimestamp > now,
    );
    this.save();
    this.notify();
  }
}
